import React, { useEffect, useMemo, useState } from "react";
import { CUSTOM_REASON_VALUE } from "../constants/moderationReasons";
import "./ConfirmPopup.scss";

const DEFAULT_SUPPRESS_NOTIFICATION = false;

const ConfirmPopup = ({
	isOpen,
	onConfirm,
	onCancel,
	title = "Вы уверены?",
	withMessageInput = false,
	withSkipNotificationToggle = false,
	skipNotificationLabel = "Не отправлять уведомление",
	messageLabel = "Сообщение пользователю (необязательно)",
	messagePlaceholder = "Введите текст уведомления...",
	reasonOptions = [],
	reasonSelectLabel = "Причина",
	customReasonLabel = "Свой вариант",
	confirmText = "Да",
	cancelText = "Нет",
	isSubmitting = false,
}) => {
	const [message, setMessage] = useState("");
	const [skipNotification, setSkipNotification] = useState(
		DEFAULT_SUPPRESS_NOTIFICATION,
	);
	const [selectedReason, setSelectedReason] = useState(CUSTOM_REASON_VALUE);

	const hasReasonOptions = useMemo(
		() => Array.isArray(reasonOptions) && reasonOptions.length > 0,
		[reasonOptions],
	);

	useEffect(() => {
		if (!isOpen) {
			setMessage("");
			setSkipNotification(DEFAULT_SUPPRESS_NOTIFICATION);
			setSelectedReason(CUSTOM_REASON_VALUE);
			return;
		}

		if (!withMessageInput) {
			setMessage("");
		}

		if (withSkipNotificationToggle) {
			setSkipNotification(DEFAULT_SUPPRESS_NOTIFICATION);
		} else {
			setSkipNotification(false);
		}

		if (hasReasonOptions) {
			setSelectedReason(CUSTOM_REASON_VALUE);
		} else {
			setSelectedReason("");
		}
	}, [isOpen, withMessageInput, withSkipNotificationToggle, hasReasonOptions]);

	if (!isOpen) return null;

	const handleReasonChange = (nextValue) => {
		setSelectedReason(nextValue);
		setMessage(nextValue === CUSTOM_REASON_VALUE ? "" : nextValue);
	};

	return (
		<div className="confirm-popup-overlay">
			<div className="confirm-popup">
				<p>{title}</p>

				{withMessageInput && (
					<>
						{hasReasonOptions && (
							<label className="confirm-popup-reason">
								<span>{reasonSelectLabel}</span>
								<div className="confirm-popup-reason-list">
									<button
										type="button"
										className={`confirm-popup-reason-item ${
											selectedReason === CUSTOM_REASON_VALUE ? "active" : ""
										}`}
										onClick={() => handleReasonChange(CUSTOM_REASON_VALUE)}
										disabled={isSubmitting}
									>
										{customReasonLabel}
									</button>

									{reasonOptions.map((option) => (
										<button
											type="button"
											key={option}
											className={`confirm-popup-reason-item ${
												selectedReason === option ? "active" : ""
											}`}
											onClick={() => handleReasonChange(option)}
											disabled={isSubmitting}
										>
											{option}
										</button>
									))}
								</div>
							</label>
						)}

						<label className="confirm-popup-message">
							<span>{messageLabel}</span>
							<textarea
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								placeholder={messagePlaceholder}
								maxLength={500}
								disabled={isSubmitting}
							/>
						</label>
					</>
				)}

				{withSkipNotificationToggle && (
					<label className="confirm-popup-check">
						<input
							type="checkbox"
							checked={skipNotification}
							onChange={(e) => setSkipNotification(e.target.checked)}
							disabled={isSubmitting}
						/>
						<span>{skipNotificationLabel}</span>
					</label>
				)}

				<div className="confirm-popup-buttons">
					<button
						className="confirm-popup-button yes-button"
						onClick={() => onConfirm?.(message.trim(), skipNotification)}
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<span
								className="confirm-popup-spinner"
								aria-label="Загрузка"
								role="status"
							/>
						) : (
							confirmText
						)}
					</button>
					<button
						className="confirm-popup-button no-button"
						onClick={onCancel}
						disabled={isSubmitting}
					>
						{cancelText}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ConfirmPopup;
